import { useEffect, useMemo, useRef, useState } from 'react';
import { FloppyDisk, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ContentDetail from './ContentDetail';
import CarouselTemplateStudioV3 from './content-detail/CarouselTemplateStudioV3';
import ContextHelpButton from './ContextHelpButton';

function buildAssetInput(asset, totalSlides) {
  const raw = asset?.slide_position === 'last' ? totalSlides : (parseInt(asset?.slide_position, 10) || 1);
  const slide = Math.min(totalSlides, Math.max(1, raw));
  const result = { media_id: asset.id, slide_numbers: [slide] };
  if (asset?.focal_point && typeof asset.focal_point.x === 'number' && typeof asset.focal_point.y === 'number') {
    result.focal_point_x = asset.focal_point.x;
    result.focal_point_y = asset.focal_point.y;
  }
  return result;
}

function stripLegacySlideLabel(value) {
  return String(value || '').replace(/^\s*SLIDE\s+\d+\s*:\s*/i, '').trim();
}

function legacySlideToSemantic(value, index, total) {
  const text = stripLegacySlideLabel(value);
  const parts = text.split(/\n\s*\n+/).map(part => part.trim()).filter(Boolean);
  return {
    slide_number: index + 1,
    role: index === 0 ? 'hook' : (index === total - 1 ? 'closing' : 'argument'),
    h1: index === 0 ? (parts[0] || text) : '',
    h2: '',
    h3: '',
    paragraph: index === 0 ? parts.slice(1).join('\n\n') : text,
    cta: '',
    formatting: normalizeFormatting({}),
  };
}

const SEMANTIC_FORMAT_FIELDS = ['h1', 'h2', 'h3', 'paragraph', 'cta'];
const savedSemanticDrafts = new Map();
function normalizeFormatting(value) {
  const source = value && typeof value === 'object' ? value : {};
  return Object.fromEntries(SEMANTIC_FORMAT_FIELDS.map(field => {
    const style = source[field] && typeof source[field] === 'object' ? source[field] : {};
    return [field, { bold: Boolean(style.bold), underline: Boolean(style.underline), italic: Boolean(style.italic) }];
  }));
}

function normalizeSemanticSlides(content) {
  const semantic = Array.isArray(content?.semantic_slides) ? content.semantic_slides : [];
  const legacy = Array.isArray(content?.slides) ? content.slides : [];
  const total = semantic.length || legacy.length;
  if (!total) return [];
  return Array.from({ length: total }, (_, index) => {
    const raw = semantic[index];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return {
        slide_number: index + 1,
        role: raw.role || (index === 0 ? 'hook' : (index === total - 1 ? 'closing' : 'argument')),
        h1: String(raw.h1 || ''),
        h2: String(raw.h2 || ''),
        h3: String(raw.h3 || ''),
        paragraph: String(raw.paragraph || ''),
        cta: String(raw.cta || ''),
        formatting: normalizeFormatting(raw.formatting),
      };
    }
    return legacySlideToSemantic(legacy[index], index, total);
  });
}

function semanticSlideToLegacy(slide) {
  return ['h3', 'h1', 'h2', 'paragraph', 'cta']
    .map(field => String(slide?.[field] || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

function CarouselSemanticCopyEditor({ api, content, onUpdate, slides, setSlides }) {
  const [saving, setSaving] = useState(false);

  const updateField = (index, field, value) => {
    setSlides(current => current.map((slide, slideIndex) => (
      slideIndex === index ? { ...slide, [field]: value } : slide
    )));
  };

  const toggleFormatting = (index, field, key) => {
    setSlides(current => current.map((slide, slideIndex) => {
      if (slideIndex !== index) return slide;
      const formatting = normalizeFormatting(slide.formatting);
      return { ...slide, formatting: { ...formatting, [field]: { ...formatting[field], [key]: !formatting[field][key] } } };
    }));
  };

  const save = async () => {
    if (!slides.length) return false;
    const legacySlides = slides.map(semanticSlideToLegacy);
    const payload = {
      semantic_slides: slides.map((slide, index) => ({ ...slide, slide_number: index + 1 })),
      slides: legacySlides,
      script: legacySlides.join('\n\n-----\n\n'),
    };
    setSaving(true);
    try {
      await api.put(`/contents/${content.id}`, payload);
      const savedSlides = payload.semantic_slides.map(slide => ({ ...slide, formatting: normalizeFormatting(slide.formatting) }));
      savedSemanticDrafts.set(content.id, savedSlides);
      setSlides(savedSlides);
      onUpdate?.({ ...content, ...payload, semantic_slides: savedSlides });
      toast.success('Testo carousel salvato');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Salvataggio testo carousel fallito');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!slides.length) return null;

  return (
    <div className="mb-5 rounded-2xl border border-[var(--border-color)] bg-black/10 p-4 space-y-4" data-testid="carousel-semantic-copy-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-[.18em] text-[var(--accent-purple)] font-semibold">Testo Carousel V3</p>
            <ContextHelpButton articleId="carousel-copy" label="Guida contenuti Carousel" />
          </div>
          <h3 className="text-sm font-semibold mt-1">Gerarchia semantica delle slide</h3>
          <p className="text-[11px] text-[var(--text-muted)] mt-1">H1, H2, H3 e Paragraph sono contenuti distinti. Il template decide solo come mostrarli.</p>
        </div>
        <button type="button" className="btn-gradient text-xs" onClick={save} disabled={saving}>
          <FloppyDisk size={14} /> {saving ? 'Salvataggio…' : 'Salva testo'}
        </button>
      </div>

      <div className="space-y-3">
        {slides.map((slide, index) => (
          <div key={slide.slide_number || index} className="rounded-xl border border-[var(--border-color)] p-3 space-y-3" data-testid={`semantic-slide-${index + 1}`}>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold">Slide {index + 1}</p>
              <select className="input-dark text-[11px] w-36" value={slide.role || 'argument'} onChange={event => updateField(index, 'role', event.target.value)}>
                <option value="hook">Hook</option>
                <option value="argument">Argument</option>
                <option value="reframe">Reframe</option>
                <option value="method">Method</option>
                <option value="insight">Insight</option>
                <option value="contrast">Contrast</option>
                <option value="reflection">Reflection</option>
                <option value="engagement">Engagement</option>
                <option value="closing">Closing</option>
              </select>
            </div>
            <SemanticField label="H3" value={slide.h3} onChange={value => updateField(index, 'h3', value)} rows={1} formatting={normalizeFormatting(slide.formatting).h3} onToggle={key => toggleFormatting(index, 'h3', key)} onSave={save} saving={saving} />
            <SemanticField label="H1" value={slide.h1} onChange={value => updateField(index, 'h1', value)} rows={2} formatting={normalizeFormatting(slide.formatting).h1} onToggle={key => toggleFormatting(index, 'h1', key)} onSave={save} saving={saving} />
            <SemanticField label="H2" value={slide.h2} onChange={value => updateField(index, 'h2', value)} rows={2} formatting={normalizeFormatting(slide.formatting).h2} onToggle={key => toggleFormatting(index, 'h2', key)} onSave={save} saving={saving} />
            <SemanticField label="Paragraph" value={slide.paragraph} onChange={value => updateField(index, 'paragraph', value)} rows={4} formatting={normalizeFormatting(slide.formatting).paragraph} onToggle={key => toggleFormatting(index, 'paragraph', key)} onSave={save} saving={saving} />
            <SemanticField label="CTA" value={slide.cta} onChange={value => updateField(index, 'cta', value)} rows={1} formatting={normalizeFormatting(slide.formatting).cta} onToggle={key => toggleFormatting(index, 'cta', key)} onSave={save} saving={saving} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SemanticField({ label, value, onChange, rows, formatting, onToggle, onSave, saving }) {
  const style = formatting || {};
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const visible = hovered || editing;
  const saveField = async () => {
    const saved = await onSave();
    if (saved) setEditing(false);
  };
  const toolClass = active => `h-7 w-7 inline-flex items-center justify-center rounded-md transition-colors hover:bg-white/10 ${active ? 'text-fuchsia-300 bg-fuchsia-500/10' : 'text-[var(--text-muted)]'}`;
  return (
    <div className="block group/semantic-field" data-testid={`semantic-field-${label.toLowerCase()}`} onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}>
      <div className="flex items-center justify-between gap-2 mb-1 min-h-7">
        <span className="text-[10px] uppercase tracking-[.12em] text-[var(--text-muted)]">{label}</span>
        <div className={`flex items-center gap-0.5 transition-opacity ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-hidden={!visible} data-testid={`semantic-toolbar-${label.toLowerCase()}`}>
          <button type="button" className={`${toolClass(style.bold)} font-bold`} aria-label={`Grassetto ${label}`} aria-pressed={Boolean(style.bold)} onClick={()=>{setEditing(true);onToggle('bold');}}>B</button>
          <button type="button" className={`${toolClass(style.italic)} italic`} aria-label={`Corsivo ${label}`} aria-pressed={Boolean(style.italic)} onClick={()=>{setEditing(true);onToggle('italic');}}>I</button>
          <button type="button" className={`${toolClass(style.underline)} underline`} aria-label={`Sottolineato ${label}`} aria-pressed={Boolean(style.underline)} onClick={()=>{setEditing(true);onToggle('underline');}}>U</button>
          <span className="h-4 w-px bg-[var(--border-color)] mx-0.5" aria-hidden="true"/>
          <button type="button" className={toolClass(false)} aria-label={`Salva ${label}`} onClick={saveField} disabled={saving}><FloppyDisk size={13}/></button>
        </div>
      </div>
      <textarea className="input-dark w-full text-xs" aria-label={label} rows={rows} value={value || ''} onFocus={()=>setEditing(true)} onChange={event => {setEditing(true);onChange(event.target.value);}} style={{fontWeight:style.bold?700:400,fontStyle:style.italic?'italic':'normal',textDecoration:style.underline?'underline':'none'}} />
    </div>
  );
}

export default function ContentDetailV3(props) {
  const { api, effectivePlan, user } = useAuth();
  const { t } = useTranslation();
  const { content, project } = props;
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const legacyRootRef = useRef(null);
  const semanticDraftContentIdRef = useRef(content?.id || null);
  const [semanticDraft, setSemanticDraft] = useState(() => savedSemanticDrafts.get(content?.id) || normalizeSemanticSlides(content));
  const semanticSlides = useMemo(() => normalizeSemanticSlides(content), [content]);
  const slides = useMemo(() => semanticSlides.map(semanticSlideToLegacy), [semanticSlides]);
  const assets = useMemo(() => Array.isArray(content?.carousel_reference_assets) ? content.carousel_reference_assets : [], [content?.carousel_reference_assets]);
  const isCarousel = content?.format === 'carousel' && semanticSlides.length >= 4;
  const canUseDesignV3 = user?.role === 'admin' || ['strategist', 'custom', 'admin'].includes(String(effectivePlan || user?.plan || '').toLowerCase());

  useEffect(() => {
    if (!content?.id || semanticDraftContentIdRef.current === content.id) return;
    setSemanticDraft(savedSemanticDrafts.get(content.id) || normalizeSemanticSlides(content));
    semanticDraftContentIdRef.current = content.id;
  }, [content]);

  useEffect(() => {
    if (!isCarousel || !legacyRootRef.current) return undefined;
    const root = legacyRootRef.current;
    let hiddenNode = null;
    const hideLegacyScript = () => {
      const expected = String(content?.script || '').trim();
      const candidates = Array.from(root.querySelectorAll('textarea.input-dark'));
      const textarea = candidates.find(node => String(node.value || '').trim() === expected && expected);
      if (textarea?.parentElement) {
        hiddenNode = textarea.parentElement;
        hiddenNode.style.display = 'none';
        hiddenNode.setAttribute('data-v3-hidden-legacy-carousel-script', 'true');
      }
    };
    hideLegacyScript();
    const observer = new MutationObserver(hideLegacyScript);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (hiddenNode) {
        hiddenNode.style.display = '';
        hiddenNode.removeAttribute('data-v3-hidden-legacy-carousel-script');
      }
    };
  }, [content?.id, content?.script, isCarousel]);

  const generate = async (templateAssignments, studioAssets = assets, studioSlides = semanticDraft, generateAiImages = false) => {
    setSubmitting(true);
    try {
      const totalSlides = studioSlides.length || slides.length;
      const payload = {
        content_id: content.id,
        project_id: project.id,
        slides_count: totalSlides,
        preset_key: 'auto',
        background_mode: 'dark',
        assets: studioAssets.map(asset => buildAssetInput(asset, totalSlides)),
        template_assignments: templateAssignments,
        generate_ai_images: Boolean(generateAiImages),
      };
      const { data } = await api.post('/media/render-carousel-design-document', payload);
      if (data?.job_id) {
        props.onUpdate?.({
          ...content,
          semantic_slides: studioSlides,
          slides: studioSlides.map(semanticSlideToLegacy),
          carousel_reference_assets: studioAssets,
          carousel_template_assignments: templateAssignments,
          carousel_render_job: {
            job_id: data.job_id,
            status: data.status || 'processing',
          },
        });
      }
      toast.success('Carousel V3 avviato. Ti avviso appena il render è pronto.');
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Generazione Carousel V3 fallita');
    } finally {
      setSubmitting(false);
    }
  };

  const routeLegacyCarouselEntryToV3 = event => {
    if (!isCarousel || !canUseDesignV3) return;
    const button = event.target?.closest?.('button');
    if (!button) return;
    const expected = String(t('editor.generateCarouselSlides') || '').trim();
    const actual = String(button.textContent || '').trim();
    if (!expected || actual !== expected) return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  const studioContent = useMemo(() => ({ ...content, semantic_slides: semanticDraft, slides: semanticDraft.map(semanticSlideToLegacy) }), [content, semanticDraft]);

  const carouselCopyEditor = isCarousel
    ? <CarouselSemanticCopyEditor api={api} content={content} onUpdate={props.onUpdate} slides={semanticDraft} setSlides={setSemanticDraft} />
    : null;

  const handleStudioContentChange = (nextSlides, nextAssets, nextAssignments) => {
    const normalized = nextSlides.map((slide, index) => ({ ...slide, slide_number: index + 1, formatting: normalizeFormatting(slide.formatting) }));
    savedSemanticDrafts.set(content.id, normalized);
    setSemanticDraft(normalized);
    props.onUpdate?.({
      ...content,
      semantic_slides: normalized,
      slides: normalized.map(semanticSlideToLegacy),
      script: normalized.map(semanticSlideToLegacy).join('\n\n-----\n\n'),
      carousel_reference_assets: nextAssets,
      carousel_template_assignments: nextAssignments,
    });
  };

  return <>
    <div ref={legacyRootRef} data-testid="content-detail-v3-router" onClickCapture={routeLegacyCarouselEntryToV3}>
      <ContentDetail {...props} carouselCopyEditor={carouselCopyEditor} />
    </div>

    {open && <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.78)', backdropFilter: 'blur(6px)' }} onClick={() => !submitting && setOpen(false)}>
      <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl flex flex-col" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }} onClick={event => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 border-b border-[var(--border-color)]">
          <div><div className="flex items-center gap-2"><p className="text-[10px] uppercase tracking-[.18em] text-[var(--accent-purple)] font-semibold">Design V3</p><ContextHelpButton articleId="carousel-studio" label="Guida Carousel Studio" /></div><h2 className="text-lg font-bold">Assegna i template alle slide</h2><p className="text-xs text-[var(--text-muted)] mt-1">Contenuto + Template + Foto = Slide. L’AI non sceglie più il layout al posto tuo.</p></div>
          <button className="btn-ghost p-2" onClick={() => setOpen(false)} disabled={submitting}><X size={17}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <CarouselTemplateStudioV3 api={api} projectId={project.id} content={studioContent} assets={assets} disabled={submitting} allowAiImages={['strategist', 'custom', 'admin'].includes(effectivePlan)} onGenerate={generate} onContentChange={handleStudioContentChange}/>
        </div>
      </div>
    </div>}
  </>;
}